<template>
    <Datepicker
        v-model="date"
        :range="isRangePicker"
        :preset-dates="presetRanges"
        :multi-calendars="showMultiCalendar"
        :multi-static="false"
        :placeholder="props.label"
        :enable-time-picker="enableTimePicker"
        :time-picker="enableTimePicker"
        :enable-seconds="false"
        :format="format"
        :preview-format="previewFormat"
        :week-start="weekStart"
        :partial-range="false"
        :min-date="minDate"
        :max-date="maxDate"
        :time-picker-inline="timePickerInline"
        :clearable="false"
        :is24="false"
        :action-row="{ showSelect: true, showCancel: true, showNow: false, showPreview: true }"
        @update:model-value="handleDate"
        @cleared="handleClear"
    >
        <template
            v-if="hasSlot('dp-input')"
            #dp-input="{ value }"
        >
            <slot
                name="dp-input"
                :value="value"
            />
        </template>
    </Datepicker>
</template>

<script setup lang="ts">
import {
    ref, watch, useSlots
} from 'vue';
import type { PropType } from 'vue';
import Datepicker from '@vuepic/vue-datepicker';
import '@vuepic/vue-datepicker/dist/main.css';
import { startOfDay, endOfDay } from 'date-fns';
import type { DateOptions } from '@agyletime/vue-3-common/src/utils/preset';
import { getPresetFromRange } from '@agyletime/vue-3-common/src/utils/preset';
import { getPresetRanges } from '@/utils/dateRange';

const props = defineProps({
    modelValue: {
        type: Object as PropType<{ range: Date | Date[] }>,
        default: () => ({ range: [] as Date[] })
    },
    label: {
        type: String,
        required: true
    },
    weekStart: {
        type: Number as PropType<DateOptions['weekStartsOn']>,
        default: 1
    },
    minDate: {
        type: Date,
        default: undefined
    },
    maxDate: {
        type: Date,
        default: undefined
    },
    showPreset: {
        type: Boolean,
        default: false
    },
    enableTimePicker: {
        type: Boolean,
        default: false
    },
    showMultiCalendar: {
        type: Boolean,
        default: true
    },
    timePickerInline: {
        type: Boolean,
        default: true
    },
    clearable: {
        type: Boolean,
        default: true
    },
    isRangePicker: {
        type: Boolean,
        default: true
    }
});

const emit = defineEmits(['update:modelValue']);

const date = ref<Date | Date[] | null>();

// Function to calculate preset ranges based on week start
const calculatePresetRanges = (weekStart: number | undefined) => {
    if (!props.showPreset || props.enableTimePicker) {
        return [];
    }

    const weekOptions: DateOptions = { weekStartsOn: (weekStart ?? 1) as DateOptions['weekStartsOn'] };
    const basePresets = getPresetRanges(weekOptions);
    
    return basePresets.map((preset) => {
        if (preset.value.length !== 2) {
            return preset;
        }
        return {
            ...preset,
            value: [ startOfDay(preset.value[0]), endOfDay(preset.value[1]) ]
        };
    });
};

// Initialize preset ranges with default week start
const presetRanges = ref(calculatePresetRanges(props.weekStart));

// Watch for weekStart prop changes and recalculate preset ranges
watch(() => props.weekStart, (newWeekStart) => {
    presetRanges.value = calculatePresetRanges(newWeekStart);
}, { immediate: false }); // Don't run immediately since we already initialized

const handleDate = (modelData: Date | Date[]) => {
    if (props.isRangePicker) {
        const range = Array.isArray(modelData) ? modelData : [modelData];
        const preset = !props.enableTimePicker
            ? getPresetFromRange(range, presetRanges.value)?.label
            : undefined;

        emit('update:modelValue', { range, preset });
    } else {
        emit('update:modelValue', { range: modelData });
    }
};

const handleClear = () => {
    emit('update:modelValue', { range: [] });
};

const hasSlot = (slotName: string) => {
    const slots = useSlots();
    return !!slots[slotName];
};

function formatDate(date: Date): string {
    const day = String(date.getDate()).padStart(2, '0');
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const year = date.getFullYear();
    return `${day}-${month}-${year}`;
}

function formatTime(date: Date): string {
    return date.toLocaleTimeString([], {
        hour: '2-digit',
        minute: '2-digit',
        hour12: true
    });
}

function format(selectedDateRange: Date[]): string {
    if (!selectedDateRange?.length) return '';

    if (props.enableTimePicker) {
        return `${formatTime(selectedDateRange[0])} - ${formatTime(selectedDateRange[1])}`;
    }

    return `${formatDate(selectedDateRange[0])} - ${formatDate(selectedDateRange[1])}`;
}

function previewFormat(selectedDateRange: Date[]): string {
    if (!selectedDateRange?.length || selectedDateRange.length !== 2) return '';

    if (props.enableTimePicker) {
        return `${formatTime(selectedDateRange[0])} - ${formatTime(selectedDateRange[1])}`;
    }

    return `${formatDate(selectedDateRange[0])} - ${formatDate(selectedDateRange[1])}`;
}

watch(
    () => props.modelValue.range,
    (newVal) => {
        if (!props.isRangePicker) {
            date.value = newVal ?? null;
            return;
        }

        const rangeArray = Array.isArray(newVal) ? newVal : [];

        if (rangeArray.length === 2 && !props.enableTimePicker) {
            date.value = [
                startOfDay(new Date(rangeArray[0])),
                endOfDay(new Date(rangeArray[1]))
            ];
        } else {
            date.value = rangeArray;
        }
    },
    { immediate: true }
);
</script>

<style lang="scss" src="./DatePicker.scss"></style>
